import { useEffect, useMemo, useState } from "react";
import { useLocalization } from "../../hooks/useLocalization";
import { useCookieConsent } from "../../context/CookieConsentProvider";
import { readAdsConsent, writeAdsConsent, type AdsConsentState } from "../../ads/consent";
import { setAdsConsentMode, setDefaultAdsConsentMode } from "../../ads/consentMode";
import { initSessionNavigation, resetSessionNavigation } from "../../analytics/sessionAnalytics";

interface CookieConsentBannerProps {
  onChange?: (state: Exclude<AdsConsentState, "unset">) => void;
}

export const CookieConsentBanner = ({ onChange }: CookieConsentBannerProps) => {
  const { strings } = useLocalization();
  const copy = strings.COOKIES;
  const { settingsOpen, closeSettings } = useCookieConsent();

  const initial = useMemo<AdsConsentState>(() => {
    if (typeof window === "undefined") return "unset";
    return readAdsConsent();
  }, []);

  const [consent, setConsent] = useState<AdsConsentState>(initial);
  const visible = consent === "unset" || settingsOpen;

  useEffect(() => {
    setDefaultAdsConsentMode();
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;
    setConsent(readAdsConsent());
  }, [settingsOpen]);

  if (!visible) return null;

  const finish = (value: Exclude<AdsConsentState, "unset">) => {
    setConsent(value);
    closeSettings();
    onChange?.(value);
  };

  const accept = () => {
    writeAdsConsent("accepted");
    setAdsConsentMode("granted");
    initSessionNavigation();
    finish("accepted");
  };

  const reject = () => {
    writeAdsConsent("rejected");
    setAdsConsentMode("denied");
    resetSessionNavigation();
    finish("rejected");
  };

  const btnBase =
    "cursor-pointer rounded-full border border-white/[0.12] bg-white/[0.06] px-[0.9rem] py-[0.55rem] font-shentox text-[0.85rem] tracking-[0.02em] text-[#e8e8ef] hover:bg-white/[0.12]";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/55 px-4 pt-[0.95rem] pb-[calc(0.95rem+env(safe-area-inset-bottom))] backdrop-blur-[6px]"
      role="dialog"
      aria-live="polite"
      aria-label={copy.TITLE}
    >
      <div className="flex w-[min(92%,60rem)] flex-col items-stretch justify-start gap-4 rounded-[1.15rem] border border-white/[0.12] bg-[rgba(16,16,29,0.92)] px-[1.15rem] py-4 shadow-[0_24px_70px_rgba(0,0,0,0.55)] max-md:w-[94%]">
        <div className="flex items-start gap-[0.85rem]">
          <div className="min-w-0">
            <strong className="mb-[0.2rem] block text-[1.05rem] font-bold text-[#e8e8ef]">
              {settingsOpen ? copy.SETTINGS_TITLE : copy.TITLE}
            </strong>
            <p className="m-0 max-w-[52rem] font-jetbrains text-[0.86rem] leading-[1.4] text-[#a8a8bc]">
              {copy.DESCRIPTION}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 justify-end gap-[0.6rem]">
          {settingsOpen ? (
            <button type="button" className={btnBase} onClick={closeSettings}>
              {copy.CLOSE}
            </button>
          ) : null}
          <button type="button" className={btnBase} onClick={reject}>
            {copy.REJECT}
          </button>
          <button
            type="button"
            className={`${btnBase} border-[rgba(28,152,217,0.4)] bg-[rgba(28,152,217,0.18)]`}
            onClick={accept}
          >
            {copy.ACCEPT}
          </button>
        </div>
      </div>
    </div>
  );
};
